import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useParams } from 'react-router-dom';
import queryRequestItems from 'api/ShareObject/queryRequestItems';
import { ShareItem, ShareObject } from 'types/Share';
import { FormatDate } from 'components/dates/FormatDate';
import { SearchFilter } from 'types/Filters';
import { PaginationItems } from 'types/Pagination';
import removeSharedItem from 'api/ShareObject/removeSharedItem';
import queryS3Bucket from 'api/ShareObject/getS3bucket';
import getEnvironmentSalmGroups from 'api/Environment/getEnvironmentSamlGroups';
import getEnvironmentAssumeRoleUrl from 'api/Environment/getEnvironmentAssumeRoleUrl';
import { useAlert } from 'components/message/AlertContext';
import submitApproval from 'api/ShareObject/submitApproval';

export default function useRequestDetails(
    filter: SearchFilter,
    refreshItems: () => Promise<void>,
) {
    const client = useClient();
    const { openAlert } = useAlert();

    const [loading, setLoading] = useState(true);
    const [shares, setShares] = useState<ShareItem[]>([]);
    const [shareObject, setShareObject] = useState<ShareObject>();
    const [error, setError] = useState<boolean>(false);
    const [created, setCreated] = useState<string>('');
    const [datasetUri, setDatasetUri] = useState<string>('');

    const [items, setItems] = useState<PaginationItems>({
        count: 0,
        pages: 0,
        page: 0,
    });
    const params = useParams();

    const removeItemFromShareObject = async (shareItemUri: string) => {
        setLoading(true);
        const response = await client.mutate(removeSharedItem(shareItemUri));
        if (!response.errors) {
            if (params.uri) {
                const submitResponse = await client.mutate(
                    submitApproval(params.uri),
                );
                if (!submitResponse.errors) {
                    openAlert({
                        severity: 'success',
                        message: `Succesfully removed from request`,
                    });
                }
                refreshItems();
            }
        } else {
            openAlert({
                severity: 'alert',
                message: response.errors[0]?.message,
            });
        }
        setLoading(false);
    };

    const goToS3Console = async (itemName: string) => {
        if (shareObject) {
            const bucketResponse = await client.query(
                queryS3Bucket(datasetUri),
            );

            if (!bucketResponse.errors) {
                const environmentResponse = await client.query(
                    getEnvironmentSalmGroups(shareObject.principal.principalId),
                );

                if (!environmentResponse.errors) {
                    const environment = environmentResponse.data.getEnvironment;
                    var currentRole;
                    if (
                        environment.SamlReaderGroupName.includes(
                            shareObject.samlGroupName,
                        )
                    ) {
                        currentRole = { Reader: shareObject.samlGroupName };
                    } else if (
                        environment.SamlBasicDevGroupName.includes(
                            shareObject.samlGroupName,
                        )
                    ) {
                        currentRole = { BasicDev: shareObject.samlGroupName };
                    } else if (
                        environment.SamlFullDevGroupName.includes(
                            shareObject.samlGroupName,
                        )
                    ) {
                        currentRole = { FullDev: shareObject.samlGroupName };
                    } else if (
                        environment.SamlGroupName.includes(
                            shareObject.samlGroupName,
                        )
                    ) {
                        currentRole = { Admin: shareObject.samlGroupName };
                    } else {
                        currentRole = {};
                    }

                    if (Object.keys(currentRole).length > 0) {
                        const response = await client.query(
                            getEnvironmentAssumeRoleUrl({
                                environmentUri:
                                    shareObject.principal.principalId,
                                currentRole: JSON.stringify(currentRole),
                                currentAccount:
                                    shareObject.principal.AwsAccountId,
                                currentBucket:
                                    bucketResponse.data.getDataset.S3BucketName,
                                currentPrefix: itemName,
                            }),
                        );
                        if (!response.errors) {
                            window.open(
                                response.data.getEnvironmentAssumeRoleUrl,
                                '_blank',
                            );
                        } else {
                            openAlert({
                                severity: 'alert',
                                message: 'Error accessing S3 console',
                            });
                        }
                    } else {
                        openAlert({
                            severity: 'alert',
                            message: `Sorry but you don't have the permissions needed to open the S3 console`,
                        });
                    }
                } else {
                    openAlert({
                        severity: 'alert',
                        message: 'Error accessing S3 console',
                    });
                }
            } else {
                openAlert({
                    severity: 'alert',
                    message: 'Error accessing S3 console',
                });
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (params.uri) {
                    const response = await client?.query(
                        queryRequestItems(params.uri, filter),
                    );

                    if (!response.errors) {
                        setShareObject(response.data.getShareObject);
                        setDatasetUri(
                            response.data.getShareObject.dataset.datasetUri,
                        );
                        setCreated(
                            FormatDate(response.data.getShareObject.created),
                        );
                        setItems(response.data.getShareObject.items);
                        setShares(
                            response.data.getShareObject.items.nodes.sort(
                                (a: ShareItem, b: ShareItem) => {
                                    const nameA = a.itemName.toUpperCase(); // ignore upper and lowercase
                                    const nameB = b.itemName.toUpperCase(); // ignore upper and lowercase
                                    if (nameA < nameB) {
                                        return -1;
                                    }
                                    if (nameA > nameB) {
                                        return 1;
                                    }
                                    // names must be equal
                                    return 0;
                                },
                            ),
                        );

                        setShares(response.data.getShareObject.items.nodes);
                    } else {
                        setError(true);
                    }
                }
            } finally {
                setLoading(false);
            }
        };
        if (client) {
            fetchData();
        }
    }, [client, filter, params.uri]);
    return {
        shares,
        items,
        created,
        loading,
        error,
        datasetUri,
        goToS3Console,
        removeItemFromShareObject,
    };
}
